import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../../component/PageLayout";
import Pagination from "../../component/Pagination";
import { useFilterPagination } from "../../component/Pagination/useFilterPagination";
import SearchBar from "../../component/SearchFilter";
import IconEdit from "../../component/SvgIcon/IconEdit";
import IconPlus from "../../component/SvgIcon/IconPlus";
import { BootstrapTooltips } from "../../services/BootstrapTooltips";
import IconLocation from "../../component/SvgIcon/IconLocation";
import { useSwal } from "../../services/SweetAlert";
import IconUpdate from "../../component/SvgIcon/IconUpdate";
import api from "../../services";
import IconShorting from "../../component/SvgIcon/IconShorting";
import { useAuth } from "../../services/AuthContext";

export default function LocationList() {
  const alert = useSwal();
  const [tableData, setTableData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [loading, setLoading] = useState(true);
  const { userType: authUserType, country: authCountry } = useAuth();

  // Pagination & Search
  const {
    searchQuery,
    setSearchQuery,
    clearSearch,
    page,
    setPage,
    totalPages,
    paginatedData,
    sortConfig,
    setSortConfig,
  } = useFilterPagination(tableData, {
    defaultPageSize: 10,
    responsivePageSize: 15,
  });

  // Table columns
  const Tablecolumns = [
    { tableid: "id", header: "ID" },
    { tableid: "Name", header: "Location Name" },
    { tableid: "Country", header: "Country" },
    { tableid: "Latitude", header: "Latitude" },
    { tableid: "Longitude", header: "Longitude" },
    { tableid: "action", header: "Actions" },
  ];

  // Fetch all locations
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const res = await api.post("/Company_locations"); // GET all locations via POST     
      if (res.data.success) {
        // ✅ Convert status to number to fix icon display after refresh
        const updatedData = res.data.data.map(row => ({
          ...row,
          status: Number(row.status),
        }));
        setTableData(updatedData);
      }
    } catch (error) {
      console.error("Failed to fetch locations: ", error);
      alert("error", "Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleEdit = (row) => {
    setEditingId(row.Id);
    setEditedRow({ ...row });
  };

  const handleUpdate = async (id) => {
    try {
      const payload = {
        Name: editedRow.Name,
        Country: editedRow.Country,
        Latitude: editedRow.Latitude,
        Longitude: editedRow.Longitude,
        status: editedRow.status,
      };

      const res = await api.put(`/Company_locations/${id}`, payload);

      if (res.data.success) {
        await fetchLocations();
        alert("success", "Location updated successfully!");
        setEditingId(null);
      } else {
        alert("error", res.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Update failed:", error);
      alert("error", "Update failed");
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      const res = await api.post(`/Company_locations/${id}/status`);
      if (res.data.success) {
        setTableData(prev =>
          prev.map(row =>
            row.Id === id ? { ...row, status: Number(res.data.data.status) } : row
          )
        );

        alert(
          "success",
          `Location ${res.data.data.status === 1 ? "activated" : "deactivated"} successfully!`
        );
      }
    } catch (error) {
      console.error("Status toggle failed:", error);
      alert("error", "Failed to toggle status");
    }
  };

  // useEffect(() => {
  //   BootstrapTooltips();
  // }, [tableData, editingId]);

  useEffect(() => {
    BootstrapTooltips();
  }, [editingId]);

  return (
    <PageLayout icon={<IconLocation />} pagetitle="Locations List">
      <div className="topspace">
        <div className="container-fluid">
          <section className="user-section mb-4">
            <div className="filterarea d-flex flex-column gap-3 flex-md-row justify-content-between align-items-center">
              <Link to="/add-location" className="btn addbtn gap-2">
                <IconPlus /> Add Location
              </Link>

              <div className="datafilter d-flex gap-2 align-items-center">
                <SearchBar
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onClear={clearSearch}
                />
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table">
                <thead className="table-light">
                  <tr>
                    {Tablecolumns.map((col, i) => (
                      <th
                        key={i}
                        className={col.tableid === "action" ? "action" : "cursor-pointer"}
                        onClick={() => {
                          if (["action", "id", "Longitude", "Latitude"].includes(col.tableid)) return;
                          setSortConfig((prev) => ({
                            key: col.tableid,
                            direction: prev.key === col.tableid && prev.direction === "asc" ? "desc" : "asc",
                          }));
                        }}
                      >
                        {col.header} {!["action", "id", "Longitude", "Latitude"].includes(col.tableid) && (
                          <IconShorting
                            activeDirection={sortConfig.key === col.tableid ? sortConfig.direction : null}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={Tablecolumns.length} className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <tr key={row.Id}>
                        <td>{index + 1}</td>
                        <td>
                          <input
                            className="form-control"
                            value={editingId === row.Id ? editedRow.Name : row.Name}
                            disabled={editingId !== row.Id}
                            onChange={(e) =>
                              editingId === row.Id &&
                              setEditedRow((prev) => ({ ...prev, Name: e.target.value }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-control"
                            value={editingId === row.Id ? editedRow.Country : row.Country}
                            disabled={editingId !== row.Id}
                            onChange={(e) =>
                              editingId === row.Id &&
                              setEditedRow((prev) => ({ ...prev, Country: e.target.value }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-control"
                            value={editingId === row.Id ? editedRow.Latitude : row.Latitude}
                            disabled={editingId !== row.Id}
                            onChange={(e) =>
                              editingId === row.Id &&
                              setEditedRow((prev) => ({ ...prev, Latitude: e.target.value }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-control"
                            value={editingId === row.Id ? editedRow.Longitude : row.Longitude}
                            disabled={editingId !== row.Id}
                            onChange={(e) =>
                              editingId === row.Id &&
                              setEditedRow((prev) => ({ ...prev, Longitude: e.target.value }))
                            }
                          />
                        </td>
                        <td className="action">
                          <div className="d-flex gap-2 flex-wrap justify-content-between">
                            {editingId === row.Id ? (
                              <button
                                className="btn-sm btn-danger"
                                onClick={() => handleUpdate(row.Id)}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Update"
                              >
                                <IconUpdate />
                              </button>
                            ) : (
                              <button
                                className="btn-sm btn-success"
                                onClick={() => handleEdit(row)}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Edit"
                                disabled={editingId !== null}
                              >
                                <IconEdit />
                              </button>
                            )}
                            {/* <button
                              className={row.status === 1 ? "btn-sm btn-success" : "btn-sm btn-secondary"}
                              onClick={async () => {
                                const confirmed = await alert(
                                  "question",
                                  `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"} this location?`
                                );
                                if (confirmed.isConfirmed) {
                                  handleStatusToggle(row.Id);
                                }
                              }}
                              disabled={editingId !== null}
                            >
                              {row.status === 1 ? <IconLocation /> : <IconLocation />}
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={Tablecolumns.length} className="text-center">
                        No records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
